import { useEffect, useState } from 'react';
import { usePageLoading } from './motion/PetalLoader';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import EditProfileForm from './EditProfileForm';
import Button from './ui/Button';

import { usePromptAuth } from './auth/AuthPrompt';
import { queryKeys } from '../lib/query/queryKeys';
// Every field the form edits. The app-wide profile (CurrentUserProfileContext) carries only the name and photo, so a
// form fed that showed blanks and saved them over the real values (the welcome step, 2026-10-26).
export const PROFILE_SELECT_OWN =
  'id, display_name, email, phone_number, profile_photo_url, instagram_handle, tiktok_handle, first_name, last_name, zip_code, avatar_hue';

export default function EditProfile() {
  const navigate = useNavigate();
  const promptAuth = usePromptAuth();
  const queryClient = useQueryClient();
  const { session, refetch: refetchCurrentUserProfile } = useCurrentUserProfile();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const pageLoad = usePageLoading(Boolean(session) && loading, { latch: Boolean(session) });

  useEffect(() => {
    if (!session?.user?.id) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function loadProfile() {
      setLoading(true);
      // Email, phone and zip are owner-only columns (165): the own row comes through get_my_profile.
      const { data, error } = await supabase
        .rpc('get_my_profile')
        .select(PROFILE_SELECT_OWN)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.error('[EditProfile] load profile failed:', error);
      }
      setProfile(data ?? null);
      setLoading(false);
    }

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  if (!session) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <p className="mb-6 text-gray-500 dark:text-dark-muted">Log in to edit your profile.</p>
        <Button size="lg" onClick={() => promptAuth()}>
          Log In
        </Button>
      </div>
    );
  }

  if (pageLoad.show) return pageLoad.node; // only the loader until the page can show whole (Sam, 2026-10-20)

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <h1 className="mb-6 text-2xl font-bold text-gray-900 dark:text-dark-text">Edit Profile</h1>

      {loading ? null : profile ? (
        <EditProfileForm
          profile={profile}
          userId={session.user.id}
          onDone={() => navigate('/profile')}
          // Each save refreshes the header's copy and drops the Profile page's, which otherwise showed the old photo and
          // name for up to 20 minutes (STALE.semiStatic) after a save (Sam, 2026-10-30: "takes a while to show")
          onSaved={async () => {
            queryClient.invalidateQueries({ queryKey: ['profiles', session.user.id] });
            queryClient.invalidateQueries({ queryKey: queryKeys.people.hues(session.user.id) });
            await refetchCurrentUserProfile();
          }}
        />
      ) : (
        <div className="rounded-lg bg-danger-fill p-4 text-danger">
          Could not load your profile.
        </div>
      )}
    </div>
  );
}
